import React, { ReactElement, useCallback, useEffect, useState, useMemo } from "react";
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/styles";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import DeleteIcon from "@mui/icons-material/Delete";
import PreviewProfileImage from "components/image-preview/profileImagePreview";
import { ImageOutlined } from "@mui/icons-material";
import {
  ContentCategoryRequest,
  ContentRequest,
  ContentResponse,
  DeleteFileRequest,
  ResponseStatus,
  Status,
} from "api/models/gamification-service/gamification_pb";
import { forceLogout, getKeycloakToken } from "config/keycloak";
import { GamificationService } from "api/models/gamification-service/gamification_pb_service";
import { grpc } from "@improbable-eng/grpc-web";
import { GAMIFICATION_SERVICE_HOST } from "api/serviceEndpoints";
import { UnaryOutput } from "@improbable-eng/grpc-web/dist/typings/unary";
import ImageCrop from "components/image-crop/image-crop";
import DialogBox from "components/dialogBox/dialog";
import { UploadEventResponse } from "components/UploadManager/UploadListItem";
import { useFileUploadService } from "services/FileUplaodService";
import { dataURLtoFile, getCroppedImg, toUploadFile } from "utils/helpers";
import { Locale } from "api/models/gamification-service/common/common_pb";
import { getAllFilters, getContentByCategory } from "redux/actions/contentActions";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { RootState } from "redux/store/store";
import { Crop } from "react-image-crop";

interface BlogsProps {
  onClickBack: any;
  isEditing: any;
  editItem: any;
  lang: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
  page: number;
  rowsPerPage: number;
}

interface State {
  editing?: boolean;
  editItem?: any;
  showDeleteModal?: boolean;
  deleteItemId?: String;
}

const initialState = {
  editing: false,
  editItem: null,
  showDeleteModal: false,
  deleteItemId: "0",
};

const PREFIX = "BlogsForm";

const classes = {
  root: `${PREFIX}-root`,
  imgCardHeader: `${PREFIX}-imgCardHeader`,
  progress: `${PREFIX}-progress`,
  formBtn: `${PREFIX}-formBtn`,
  thumbNail: `${PREFIX}-thumbNail`,
  media: `${PREFIX}-media`,
  actionIcon: `${PREFIX}-actionIcon`,
  audioPlayer: `${PREFIX}-audioPlayer`,
  paper: `${PREFIX}-paper`,
  toggle: `${PREFIX}-toggle`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    height: 250,
    width: "100%",
    //overflow: "scroll",
  },

  [`& .${classes.imgCardHeader}`]: {
    height: "20%",
    textOverflow: "elipse",
  },

  [`& .${classes.progress}`]: {
    color: theme.palette.primary.main,
    position: "absolute",
    left: "50%",
  },

  [`& .${classes.formBtn}`]: {
    marginRight: "20px",
  },

  [`& .${classes.thumbNail}`]: {
    height: "100%",
    width: "100%",
    justifyContent: "center",
    alignContent: "cenrter",
    alignItems: "center",
    display: "block",
  },

  [`& .${classes.media}`]: {
    height: "60%",
  },

  [`& .${classes.actionIcon}`]: {
    alignSelf: "center",
    left: "45%",
  },

  [`& .${classes.audioPlayer}`]: {
    height: 250,
    width: "100%",
  },

  [`& .${classes.paper}`]: {
    position: "absolute",
    width: 400,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2, 4, 3),
    top: `40%`,
    left: `40%`,
  },

  [`& .${classes.toggle}`]: {
    "&.MuiToggleButton-root": {
      padding: "15px 11px",
    },
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "white",
    },
  },
}));

export const BlogsForm = React.forwardRef<HTMLDivElement, BlogsProps>(
  (props, ref): ReactElement => {
    const cropDef: Crop = {
      x: 25,
      y: 25,
      height: 20,
      width: 20,
      unit: "%",
    };

    const blogCategoryList = [
      {
        displayName: "Featured blog",
        code: 1,
      },
      {
        displayName: "All blogs",
        code: 2,
      },
    ];

    const dispatch = useDispatch();
    const languages = useSelector((state: RootState) => state.app.languages);

    const languageType = useMemo(() => ["defaultcontent", "en", "hi", "te", "gu", "ta", "ka"], []);

    const filterList = useSelector(
      (state: RootState) => state.content.filters.filtersList.contentfilterresponseList,
    );

    const { appendFilesToUploadManager } = useFileUploadService();
    const [language, setLanguage] = useState<any>(1);
    const [states, setStates] = useState<State>(initialState);
    const [id, setId] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [author, setAuthor] = useState<string>("");
    const [readingTime, setReadingTime] = useState<number>(0);
    const [content, setContent] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [blogCategory, setBlogCategory] = useState<any>(1);
    const [selectedFilers, setSlectedFilters] = useState<string[]>([]);
    const [selectedFilersId, setSlectedFiltersId] = useState<any[]>([]);

    const [imgUrl, setImgUrl] = useState<any>(null);
    const [imageSrc, setImageSrc] = useState<any>(null);
    const [imageCrop, setImageCrop] = useState<Crop>(cropDef);
    const [imageRef, setImageRef] = useState<any>();
    const [imageFile, setImageFile] = useState<any>(null);

    const [thumbnailUrl, setThumbnailUrl] = useState<any>(null);
    const [thumbnailSrc, setThumbnailSrc] = useState<any>(null);
    const [thumbnailCrop, setThumbnailCrop] = useState<Crop>(cropDef);
    const [thumbnailRef, setThumbnailRef] = useState<any>();
    const [thumbnailFile, setThumbnailFile] = useState<any>(null);

    const [featuredImageUrl, setFeaturedImageUrl] = useState<any>(null);
    const [featuredImageSrc, setFeaturedImageSrc] = useState<any>(null);
    const [featuredImageCrop, setFeaturedImageCrop] = useState<Crop>(cropDef);
    const [featuredImageRef, setFeaturedImageRef] = useState<any>();
    const [featuredImageFile, setFeaturedImageFile] = useState<any>(null);
    const [isAdding, setIsAdding] = useState(false);

    const hiddenFileInput1: any = React.useRef(null);
    const hiddenFileInputThumbnail: any = React.useRef(null);
    const hiddenFileInputFeaturedImage: any = React.useRef(null);

    const [open, setOpen] = useState(false);
    const [isUploading, setIsUploading] = useState({
      image: false,
      thumbnail: false,
      featuredImage: false,
      selectedImage: false,
      audio: false,
    });

    const [snackBar, setSnackBar] = useState<any>({
      open: false,
      severity: "success",
      message: "",
    });

    const onClickEdit = useCallback(
      (item: any) => {
        setStates({ editing: true });
        setLanguage(props.lang);
        setId(item.id);
        setTitle(item.title);
        setDescription(item.subtitle);
        setAuthor(item.author);
        setReadingTime(item.readingtime);
        setContent(item.content);
        setImgUrl(item.image);
        setThumbnailUrl(item.thumbnail);
        setFeaturedImageUrl(item.featuredimage);
        setBlogCategory(item.featuredcontent);
        setSlectedFiltersId(item.filteridsList);
      },
      [props.lang],
    );

    useEffect(() => {
      dispatch(getAllFilters({ pageNumber: 0, pageSize: 100 }));
    }, [dispatch]);

    useEffect(() => {
      if (props.isEditing) {
        onClickEdit(props.editItem);
      }
    }, [props.isEditing, props.editItem, onClickEdit]);

    useEffect(() => {
      const temp = filterList.filter(item => selectedFilersId.includes(item.id));
      const arrOfString = temp.map(
        (item: any) => item.contentfiltername[languageType[language - 1]],
      );

      setSlectedFilters(arrOfString);
    }, [filterList, language, languageType, selectedFilersId]);

    const _deleteFileFromGcp = async (data: any) => {
      try {
        console.log("image url to delete from gcp", data);
        const jwtToken = await getKeycloakToken();
        const reqBody: DeleteFileRequest = new DeleteFileRequest();
        reqBody.setImgpath(data);
        grpc.unary(GamificationService.DeleteFileFromGCP, {
          host: GAMIFICATION_SERVICE_HOST,
          request: reqBody,
          metadata: {
            Authorization: `Bearer ${jwtToken}`,
          },
          onEnd: (res: UnaryOutput<ResponseStatus>) => {
            const { status, message, statusMessage } = res;
            console.log("statusMessage", statusMessage);
            if (status === grpc.Code.OK && message) {
              if (message) {
                console.log("data coming from delete file from gcp", message.toObject());
              }
            } else if (status === grpc.Code.Unauthenticated) forceLogout();
            else {
              setIsLoading(false);
            }
          },
        });
      } catch (err) {
        console.log("Error ", err);
      }
    };

    const handleChangeForMultipleSelect = (event: any) => {
      const {
        target: { value },
      } = event;
      console.log(value);

      const arrOfNum: number[] = (typeof value === "string" ? value.split(",") : value).map(str => {
        return Number(str);
      });

      setSlectedFiltersId(arrOfNum);
    };

    const handleUploadClick1 = () => {
      hiddenFileInput1.current.click();
    };

    const handleUploadClickThumbnail = () => {
      hiddenFileInputThumbnail.current.click();
    };

    const handleUploadClickFeaturedImage = () => {
      hiddenFileInputFeaturedImage.current.click();
    };

    const onOpenImage = () => {
      setOpen(true);
    };

    const onCloseImage = () => {
      setOpen(false);
      setImageSrc("");
      setImageCrop(cropDef);
      setIsUploading({ ...isUploading, image: false, selectedImage: false });
    };
    const onCloseThumbnail = () => {
      setOpen(false);
      setThumbnailSrc("");
      setThumbnailCrop(cropDef);
      setIsUploading({ ...isUploading, image: false, selectedImage: false });
    };
    const onCloseFeaturedImage = () => {
      setOpen(false);
      setFeaturedImageSrc("");
      setFeaturedImageCrop(cropDef);
      setIsUploading({ ...isUploading, image: false, selectedImage: false });
    };

    const onSelectImage1 = (event: any) => {
      if (event.target.files[0]) {
        setIsUploading({ ...isUploading, image: true });
        setImageFile(event.target.files[0]);
        const reader = new FileReader();
        reader.readAsDataURL(event.target.files[0]);
        reader.onloadend = () => {
          setImageSrc(reader.result);
          setOpen(true);
        };
      }
    };

    const onSelectThumbnail = (event: any) => {
      if (event.target.files[0]) {
        setIsUploading({ ...isUploading, thumbnail: true });
        setThumbnailFile(event.target.files[0]);
        const reader = new FileReader();
        reader.readAsDataURL(event.target.files[0]);
        reader.onloadend = () => {
          setThumbnailSrc(reader.result);
          setOpen(true);
        };
      }
    };

    const onSelectFeaturedImage = (event: any) => {
      if (event.target.files[0]) {
        setIsUploading({ ...isUploading, featuredImage: true });
        setFeaturedImageFile(event.target.files[0]);
        const reader = new FileReader();
        reader.readAsDataURL(event.target.files[0]);
        reader.onloadend = () => {
          setFeaturedImageSrc(reader.result);
          setOpen(true);
        };
      }
    };

    const onImageLoaded1 = (image: any) => {
      setImageRef(image);
    };

    const onCropComplete = async () => {};

    const onCropDone1 = async () => {
      const cr: any = imageCrop;
      if (imageRef && cr.width && cr.height) {
        const croppedUrl = await getCroppedImg(imageRef, cr);
        const croppedFile: File = dataURLtoFile(
          croppedUrl,
          `${new Date().getTime().toString()}_${imageFile.name.replaceAll(" ", "")}`,
        );
        setImgUrl(croppedUrl);
        const uploadFile = toUploadFile(
          croppedFile,
          `${
            language === 1
              ? "resources/blogs-images"
              : `resources/blogs-images/${languages[language - 1].displayName
                  .toLowerCase()
                  .replaceAll(" ", "")}`
          }`,
        );
        appendFilesToUploadManager([uploadFile]);
        window.addEventListener(uploadFile.token, (res: CustomEventInit<UploadEventResponse>) => {
          if (res.detail?.code === 200) {
            setImgUrl(res.detail.response);
            console.log("image url res", res.detail.response);
            setIsUploading({ ...isUploading, image: false });
          } else {
            setIsUploading({ ...isUploading, image: false });
          }
        });
      }
      setImageSrc(null);
    };

    const onCropChange1 = (crop: any) => {
      setImageCrop(crop);
    };

    const onImageClose1 = () => {
      setImageSrc(null);
      _deleteFileFromGcp(imgUrl);
      setImgUrl("");
      setImageFile(null);
      setImageCrop(cropDef);
      setIsUploading({ ...isUploading, image: false });
    };

    const onThumbnailLoaded = (image: any) => {
      setThumbnailRef(image);
    };

    const onThumbnailCropDone = async () => {
      const cr: any = thumbnailCrop;
      if (thumbnailRef && cr.width && cr.height) {
        const croppedUrl = await getCroppedImg(thumbnailRef, cr);
        const croppedFile: File = dataURLtoFile(
          croppedUrl,
          `${new Date().getTime().toString()}_${thumbnailFile.name.replaceAll(" ", "")}`,
        );
        setThumbnailUrl(croppedUrl);
        const uploadFile = toUploadFile(
          croppedFile,
          `${
            language === 1
              ? "resources/article-images/thumbnails"
              : `resources/article-images/thumbnails/${languages[language - 1].displayName
                  .toLowerCase()
                  .replaceAll(" ", "")}`
          }`,
        );
        appendFilesToUploadManager([uploadFile]);
        window.addEventListener(uploadFile.token, (res: CustomEventInit<UploadEventResponse>) => {
          if (res.detail?.code === 200) {
            setThumbnailUrl(res.detail.response);
            console.log("image url res", res.detail.response);
            setIsUploading({ ...isUploading, thumbnail: false });
          } else {
            setIsUploading({ ...isUploading, thumbnail: false });
          }
        });
      }
      setThumbnailSrc(null);
    };

    const onThumbnailCropChange = (crop: any) => {
      setThumbnailCrop(crop);
    };

    const onThumbnailClose = () => {
      setThumbnailSrc(null);
      _deleteFileFromGcp(thumbnailUrl);
      setThumbnailUrl("");
      setThumbnailFile(null);
      setThumbnailCrop(cropDef);
      setIsUploading({ ...isUploading, thumbnail: false });
    };

    const onFeaturedImageLoaded = (image: any) => {
      setFeaturedImageRef(image);
    };

    const onFeaturedImageCropDone = async () => {
      const cr: any = featuredImageCrop;
      if (featuredImageRef && cr.width && cr.height) {
        const croppedUrl = await getCroppedImg(featuredImageRef, cr);
        const croppedFile: File = dataURLtoFile(
          croppedUrl,
          `${new Date().getTime().toString()}_${featuredImageFile.name.replaceAll(" ", "")}`,
        );
        setFeaturedImageUrl(croppedUrl);
        const uploadFile = toUploadFile(
          croppedFile,
          `${
            language === 1
              ? "resources/article-images/featured-images"
              : `resources/article-images/featured-images/${languages[language - 1].displayName
                  .toLowerCase()
                  .replaceAll(" ", "")}`
          }`,
        );
        appendFilesToUploadManager([uploadFile]);
        window.addEventListener(uploadFile.token, (res: CustomEventInit<UploadEventResponse>) => {
          if (res.detail?.code === 200) {
            setFeaturedImageUrl(res.detail.response);
            console.log("image url res", res.detail.response);
            setIsUploading({ ...isUploading, featuredImage: false });
          } else {
            setIsUploading({ ...isUploading, featuredImage: false });
          }
        });
      }
      setFeaturedImageSrc(null);
    };

    const onFeaturedImageCropChange = (crop: any) => {
      setFeaturedImageCrop(crop);
    };

    const onFeaturedImageClose = () => {
      setFeaturedImageSrc(null);
      _deleteFileFromGcp(featuredImageUrl);
      setFeaturedImageUrl("");
      setFeaturedImageFile(null);
      setFeaturedImageCrop(cropDef);
      setIsUploading({ ...isUploading, featuredImage: false });
    };

    const validateForm = () => {
      if (title.trim() === "") {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Blog title can not be empty.",
        });
      } else if (author.trim() === "") {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Blog author can not be empty.",
        });
      } else if (readingTime <= 0) {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Blog reading time can not be less than or equal to zero.",
        });
      } else if (readingTime > 10000000) {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Blog reading time can not be more than 10000000",
        });
      } else if (Number(readingTime) - Math.floor(Number(readingTime)) !== 0) {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Blog reading time can not be in decimal.",
        });
      } else if (content.trim() === "") {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Blog content can not be empty.",
        });
      } else if (!imgUrl) {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Blog image can not be empty.",
        });
      } else if (!thumbnailUrl) {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Blog thumbnail can not be empty.",
        });
      } else if (!featuredImageUrl) {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Blog feature image can not be empty.",
        });
      } else if (description.trim() === "") {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Blog description can not be empty.",
        });
      } else {
        return true;
      }
    };

    const _addBlog = () => {
      var blogData: any = {};
      if (validateForm()) {
        switch (props.lang) {
          case 1:
            blogData = {
              title: {
                defaultContent: title,
                en: title,
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              description: {
                defaultContent: description,
                en: description,
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              author: {
                defaultContent: author,
                en: author,
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              content: {
                defaultContent: content,
                en: content,
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              image: imgUrl,
              thumbnail: thumbnailUrl,
              featuredImage: featuredImageUrl,
              selectedFilersId: selectedFilersId,
              blogCategory: blogCategory,
              readingTime: readingTime,
              isWebView: false,
              webViewUrl: "",
              contentCategory: 1,
              video: "",
            };
            break;
          case 2:
            blogData = {
              title: {
                defaultContent: title,
                en: title,
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              description: {
                defaultContent: description,
                en: description,
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              author: {
                defaultContent: author,
                en: author,
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              content: {
                defaultContent: content,
                en: content,
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              image: imgUrl,
              thumbnail: thumbnailUrl,
              featuredImage: featuredImageUrl,
              blogCategory: blogCategory,
              readingTime: readingTime,
              selectedFilersId: selectedFilersId,
              isWebView: false,
              webViewUrl: "",
              contentCategory: 1,
              video: "",
            };
            break;
          case 3:
            blogData = {
              title: {
                defaultContent: title,
                en: "",
                hi: title,
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              description: {
                defaultContent: description,
                en: "",
                hi: description,
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              author: {
                defaultContent: author,
                en: "",
                hi: author,
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              content: {
                defaultContent: content,
                en: "",
                hi: content,
                te: "",
                gu: "",
                ta: "",
                ka: "",
              },
              image: imgUrl,
              thumbnail: thumbnailUrl,
              featuredImage: featuredImageUrl,
              blogCategory: blogCategory,
              selectedFilersId: selectedFilersId,
              readingTime: readingTime,
              isWebView: false,
              webViewUrl: "",
              contentCategory: 1,
              video: "",
            };
            break;
          case 4:
            blogData = {
              title: {
                defaultContent: title,
                en: "",
                hi: "",
                te: title,
                gu: "",
                ta: "",
                ka: "",
              },
              description: {
                defaultContent: description,
                en: "",
                hi: "",
                te: description,
                gu: "",
                ta: "",
                ka: "",
              },
              author: {
                defaultContent: author,
                en: "",
                hi: "",
                te: author,
                gu: "",
                ta: "",
                ka: "",
              },
              content: {
                defaultContent: content,
                en: "",
                hi: "",
                te: content,
                gu: "",
                ta: "",
                ka: "",
              },
              image: imgUrl,
              thumbnail: thumbnailUrl,
              featuredImage: featuredImageUrl,
              blogCategory: blogCategory,
              readingTime: readingTime,
              selectedFilersId: selectedFilersId,
              isWebView: false,
              webViewUrl: "",
              contentCategory: 1,
              video: "",
            };
            break;
          case 5:
            blogData = {
              title: {
                defaultContent: title,
                en: "",
                hi: "",
                te: "",
                gu: title,
                ta: "",
                ka: "",
              },
              description: {
                defaultContent: description,
                en: "",
                hi: "",
                te: "",
                gu: description,
                ta: "",
                ka: "",
              },
              author: {
                defaultContent: author,
                en: "",
                hi: "",
                te: "",
                gu: author,
                ta: "",
                ka: "",
              },
              content: {
                defaultContent: content,
                en: "",
                hi: "",
                te: "",
                gu: content,
                ta: "",
                ka: "",
              },
              image: imgUrl,
              thumbnail: thumbnailUrl,
              featuredImage: featuredImageUrl,
              blogCategory: blogCategory,
              selectedFilersId: selectedFilersId,
              readingTime: readingTime,
              isWebView: false,
              webViewUrl: "",
              contentCategory: 1,
              video: "",
            };
            break;
          case 6:
            blogData = {
              title: {
                defaultContent: title,
                en: "",
                hi: "",
                te: "",
                gu: "",
                ta: title,
                ka: "",
              },
              description: {
                defaultContent: description,
                en: "",
                hi: "",
                te: "",
                gu: "",
                ta: description,
                ka: "",
              },
              author: {
                defaultContent: author,
                en: "",
                hi: "",
                te: "",
                gu: "",
                ta: author,
                ka: "",
              },
              content: {
                defaultContent: content,
                en: "",
                hi: "",
                te: "",
                gu: "",
                ta: content,
                ka: "",
              },
              image: imgUrl,
              thumbnail: thumbnailUrl,
              featuredImage: featuredImageUrl,
              blogCategory: blogCategory,
              selectedFilersId: selectedFilersId,
              readingTime: readingTime,
              isWebView: false,
              webViewUrl: "",
              contentCategory: 1,
              video: "",
            };
            break;
          case 7:
            blogData = {
              title: {
                defaultContent: title,
                en: "",
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: title,
              },
              description: {
                defaultContent: description,
                en: "",
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: description,
              },
              author: {
                defaultContent: author,
                en: "",
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: author,
              },
              content: {
                defaultContent: content,
                en: "",
                hi: "",
                te: "",
                gu: "",
                ta: "",
                ka: content,
              },
              image: imgUrl,
              thumbnail: thumbnailUrl,
              featuredImage: featuredImageUrl,
              blogCategory: blogCategory,
              readingTime: readingTime,
              selectedFilersId: selectedFilersId,
              isWebView: false,
              webViewUrl: "",
              contentCategory: 1,
              video: "",
            };
            break;
        }
        if (props.isEditing) {
          setIsAdding(true);
          updateBlog(blogData);
        } else {
          setIsAdding(true);
          addBlog(blogData);
        }
      }
    };

    const updateBlog = async (data: any) => {
      setIsLoading(true);
      try {
        console.log(`Request to updating blog ${data.title.hi}`);

        const titleLocale: Locale = new Locale();
        titleLocale.setDefaultcontent(data.title.defaultContent);
        titleLocale.setEn(data.title.en);
        titleLocale.setHi(data.title.hi);
        titleLocale.setTe(data.title.te);
        titleLocale.setGu(data.title.gu);
        titleLocale.setTa(data.title.ta);
        titleLocale.setKa(data.title.ka);

        const descriptionLocale: Locale = new Locale();
        descriptionLocale.setDefaultcontent(data.description.defaultContent);
        descriptionLocale.setEn(data.description.en);
        descriptionLocale.setHi(data.description.hi);
        descriptionLocale.setTe(data.description.te);
        descriptionLocale.setGu(data.description.gu);
        descriptionLocale.setTa(data.description.ta);
        descriptionLocale.setKa(data.description.ka);

        const authorLocale: Locale = new Locale();
        authorLocale.setDefaultcontent(data.author.defaultContent);
        authorLocale.setEn(data.author.en);
        authorLocale.setHi(data.author.hi);
        authorLocale.setTe(data.author.te);
        authorLocale.setGu(data.author.gu);
        authorLocale.setTa(data.author.ta);
        authorLocale.setKa(data.author.ka);

        const contentLocale: Locale = new Locale();
        contentLocale.setDefaultcontent(data.content.defaultContent);
        contentLocale.setEn(data.content.en);
        contentLocale.setHi(data.content.hi);
        contentLocale.setTe(data.content.te);
        contentLocale.setGu(data.content.gu);
        contentLocale.setTa(data.content.ta);
        contentLocale.setKa(data.content.ka);

        const reqBody = new ContentResponse();
        reqBody.setId(props.editItem.id);
        reqBody.setTitle(titleLocale);
        reqBody.setSubtitle(descriptionLocale);
        reqBody.setAuthor(authorLocale);
        reqBody.setContent(contentLocale);
        reqBody.setImage(data.image);
        reqBody.setThumbnail(data.thumbnail);
        reqBody.setFeatureimage(data.featuredImage);
        reqBody.setReadingtime(data.readingTime);
        reqBody.setWebviewurl(data.webViewUrl);
        reqBody.setContentcategory(2);
        reqBody.setFeaturedcontent(data.blogCategory);
        reqBody.setFilteridsList(data.selectedFilersId);

        console.log(`Request for updating blog`, reqBody.toObject());
        const jwtToken = await getKeycloakToken();

        grpc.unary(GamificationService.UpdateContent, {
          request: reqBody,
          host: GAMIFICATION_SERVICE_HOST,
          metadata: {
            Authorization: `Bearer ${jwtToken}`,
          },
          onEnd: (res: UnaryOutput<ContentResponse>) => {
            const { status, message, statusMessage } = res;
            if (status === grpc.Code.OK && message) {
              if (res.message) {
                console.log("update blog response", res.message.toObject());
                setIsLoading(false);
                dispatch(getContentByCategory(props.lang, 2, props.page, props.rowsPerPage));
                setSnackBar({
                  open: true,
                  severity: "success",
                  message: "Blog updated successfully",
                });
                setTimeout(() => props.onClickBack(), 2000);
              }
            } else if (status === grpc.Code.Unauthenticated) forceLogout();
            else {
              setSnackBar({
                open: true,
                severity: "error",
                message: `Error in updating blog : ${statusMessage}`,
              });
            }
          },
        });
      } catch (error) {
        setIsLoading(false);
        console.log(`Error in update blog ${error}`);
        setSnackBar({
          open: true,
          severity: "error",
          message: { error },
        });
      }
    };

    const addBlog = async (data: any) => {
      setIsLoading(true);
      try {
        console.log(`Request to adding new blog ${data}`);

        const titleLocale: Locale = new Locale();
        titleLocale.setDefaultcontent(data.title.defaultContent);
        titleLocale.setEn(data.title.en);
        titleLocale.setHi(data.title.hi);
        titleLocale.setTe(data.title.te);
        titleLocale.setGu(data.title.gu);
        titleLocale.setTa(data.title.ta);
        titleLocale.setKa(data.title.ka);

        const descriptionLocale: Locale = new Locale();
        descriptionLocale.setDefaultcontent(data.description.defaultContent);
        descriptionLocale.setEn(data.description.en);
        descriptionLocale.setHi(data.description.hi);
        descriptionLocale.setTe(data.description.te);
        descriptionLocale.setGu(data.description.gu);
        descriptionLocale.setTa(data.description.ta);
        descriptionLocale.setKa(data.description.ka);

        const authorLocale: Locale = new Locale();
        authorLocale.setDefaultcontent(data.author.defaultContent);
        authorLocale.setEn(data.author.en);
        authorLocale.setHi(data.author.hi);
        authorLocale.setTe(data.author.te);
        authorLocale.setGu(data.author.gu);
        authorLocale.setTa(data.author.ta);
        authorLocale.setKa(data.author.ka);

        const contentLocale: Locale = new Locale();
        contentLocale.setDefaultcontent(data.content.defaultContent);
        contentLocale.setEn(data.content.en);
        contentLocale.setHi(data.content.hi);
        contentLocale.setTe(data.content.te);
        contentLocale.setGu(data.content.gu);
        contentLocale.setTa(data.content.ta);
        contentLocale.setKa(data.content.ka);

        const reqBody = new ContentRequest();
        reqBody.setTitle(titleLocale);
        reqBody.setSubtitle(descriptionLocale);
        reqBody.setAuthor(authorLocale);
        reqBody.setContent(contentLocale);
        reqBody.setImage(data.image);
        reqBody.setThumbnail(data.thumbnail);
        reqBody.setFeatureimage(data.featuredImage);
        reqBody.setReadingtime(data.readingTime);
        reqBody.setIswebview(data.isWebView);
        reqBody.setWebviewurl(data.webViewUrl);
        reqBody.setContentcategory(2);
        reqBody.setFeaturedcontent(data.blogCategory);
        reqBody.setFilteridsList(data.selectedFilersId);

        console.log(`Request for adding blog ${reqBody}`);
        const jwtToken = await getKeycloakToken();

        grpc.unary(GamificationService.AddContent, {
          request: reqBody,
          host: GAMIFICATION_SERVICE_HOST,
          metadata: {
            Authorization: `Bearer ${jwtToken}`,
          },
          onEnd: (res: UnaryOutput<ContentResponse>) => {
            const { status, message, statusMessage } = res;
            if (status === grpc.Code.OK && message) {
              if (res.message) {
                console.log("add blog response", res.message.toObject());
                setIsLoading(false);
                dispatch(getContentByCategory(props.lang, 2, props.page, props.rowsPerPage));
                setSnackBar({
                  open: true,
                  severity: "success",
                  message: "Blog added successfully",
                });
                setTimeout(() => props.onClickBack(), 2000);
              }
            } else if (status === grpc.Code.Unauthenticated) forceLogout();
            else {
              setSnackBar({
                open: true,
                severity: "error",
                message: `Error in adding blog : ${statusMessage}`,
              });
            }
          },
        });
      } catch (error) {
        setIsLoading(false);
        console.log(`Error in add blog ${error}`);
        setSnackBar({
          open: true,
          severity: "error",
          message: { error },
        });
      }
    };

    const onClickDelete = (blogId: any) => {
      setStates({ showDeleteModal: true, deleteItemId: blogId });
    };

    const handleDeleteClose = () => {
      setStates({ showDeleteModal: false });
    };

    const deleteBlog = async () => {
      deleteBlogById(states.deleteItemId);
    };

    const deleteBlogById = async (blogId: String | undefined) => {
      setIsLoading(true);
      console.log(`Delete call for blog with id ${blogId}`);
      try {
        const jwtToken = await getKeycloakToken();
        const reqBody: ContentCategoryRequest = new ContentCategoryRequest();
        reqBody.setContentid(Number(blogId));
        reqBody.setContentcategory(2);
        grpc.unary(GamificationService.DeleteContentByIDAndCategory, {
          host: GAMIFICATION_SERVICE_HOST,
          request: reqBody,
          metadata: {
            Authorization: `Bearer ${jwtToken}`,
          },
          onEnd: (res: UnaryOutput<Status>) => {
            const { status, message } = res;
            if (status === grpc.Code.OK && message) {
              if (message) {
                console.log(`Delete blog response ${message.toObject()}`);
                setIsLoading(false);
                _deleteFileFromGcp(imgUrl);
                setStates({ showDeleteModal: false });
                setTimeout(() => props.onClickBack(), 2000);
                dispatch(getContentByCategory(language, 2, props.page, props.rowsPerPage));
                setSnackBar({
                  open: true,
                  severity: "success",
                  message: " Deleted blog successfully",
                });
              } else {
                setIsLoading(false);
                setStates({ showDeleteModal: false });
                setSnackBar({
                  open: true,
                  severity: "Error",
                  message: "Error in deleting blog",
                });
              }
            } else if (status === grpc.Code.Unauthenticated) forceLogout();
          },
        });
      } catch (err) {
        setIsLoading(false);
        console.log(`Error: ${err}`);
      }
    };

    return (
      <Root ref={ref} className={classes.root}>
        {isLoading && (
          <CircularProgress style={{ position: "absolute", top: "50%", right: "50%" }} />
        )}
        <Grid container className="p-2 align-items-center" justifyContent="space-between">
          <Grid item>
            <Grid container alignItems="center">
              <Grid>
                <IconButton onClick={props.onClickBack} size="large">
                  <KeyboardBackspaceIcon color="action" />
                </IconButton>
              </Grid>
              <Grid item>
                <Typography variant="h6">
                  {props.isEditing ? "Edit Blog" : "Add New Blog"}
                </Typography>
                {props.isEditing ? (
                  <Breadcrumbs aria-label="breadcrumb">
                    <Typography
                      color="inherit"
                      style={{ cursor: "pointer" }}
                      onClick={props.onClickBack}
                    >
                      Blogs
                    </Typography>
                    <Tooltip
                      title={props.editItem.title}
                      disableHoverListener={props.editItem.title?.length < 20}
                      placement="bottom"
                    >
                      <Box>
                        <Typography color="inherit" style={{ cursor: "pointer" }}>
                          {props.editItem.title?.slice(0, 20)}
                          {props.editItem.title?.length > 20 && "..."}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Typography color="textPrimary">Edit</Typography>
                  </Breadcrumbs>
                ) : null}
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            {props.isEditing ? (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteIcon />}
                  onClick={() => onClickDelete(id)}
                >
                  Delete
                </Button>
              </>
            ) : null}
          </Grid>
        </Grid>
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item sm={6} xs={12}>
              <TextField
                fullWidth
                required
                type="text"
                name="title"
                label="Title"
                variant="outlined"
                value={title}
                onChange={e => {
                  setTitle(e.target.value);
                }}
                className="mr-3"
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                fullWidth
                required
                type="text"
                name="author"
                label="Author"
                variant="outlined"
                value={author}
                onChange={e => {
                  setAuthor(e.target.value);
                }}
                className="mr-3"
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                fullWidth
                required
                type="number"
                name="readingTime"
                label="Reading Time (min)"
                variant="outlined"
                value={readingTime}
                onChange={e => {
                  setReadingTime(Number(e.target.value));
                }}
                className="mr-3"
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel id="demo-multiple-checkbox-label">Filters</InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={selectedFilersId}
                  onChange={handleChangeForMultipleSelect}
                  input={<OutlinedInput label="Filters" />}
                  renderValue={() => selectedFilers.join(", ")}
                >
                  {filterList.map((x: any) => (
                    <MenuItem key={x.id} value={x.id}>
                      <Checkbox checked={selectedFilersId.indexOf(x.id) > -1} />
                      <ListItemText
                        primary={String(x.contentfiltername[languageType[language - 1]])}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="language-select">Category</InputLabel>
                <Select
                  defaultValue=""
                  labelId="video-category-select"
                  value={blogCategory}
                  label="Language"
                  onChange={e => {
                    setBlogCategory(e.target.value as number);
                  }}
                >
                  {blogCategoryList.map((catagory: any, i: any) => (
                    <MenuItem key={i} value={catagory.code}>
                      {catagory.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={4} xs={12}>
              <FormControl variant="outlined" fullWidth>
                <Card variant="outlined" className={classes.root}>
                  {imgUrl ? (
                    <PreviewProfileImage onImageClose={onImageClose1} src={imgUrl} rectangular />
                  ) : (
                    <>
                      {isUploading.image ? (
                        <Card className={classes.thumbNail}>
                          <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                            className="pt-5"
                          >
                            <Typography className="pt-4">Uploading...</Typography>
                          </Grid>
                        </Card>
                      ) : (
                        <CardActionArea
                          className={classes.root}
                          onClick={() => {
                            handleUploadClick1();
                          }}
                        >
                          {/* <InputLabel htmlFor="thumbnail">
                            Upload image
                          </InputLabel> */}
                          <Avatar className={classes.actionIcon} variant="square">
                            <ImageOutlined />
                          </Avatar>
                          <input
                            accept="image/*"
                            id="contained-button-file"
                            type="file"
                            hidden
                            onChange={onSelectImage1}
                            ref={hiddenFileInput1}
                          />
                        </CardActionArea>
                      )}
                    </>
                  )}
                </Card>
                <Typography variant="overline" align="center" gutterBottom>
                  Image
                </Typography>
              </FormControl>
            </Grid>
            <Grid item md={4} xs={12}>
              <FormControl variant="outlined" fullWidth>
                <Card variant="outlined" className={classes.root}>
                  {thumbnailUrl ? (
                    <PreviewProfileImage
                      onImageClose={onThumbnailClose}
                      src={thumbnailUrl}
                      rectangular
                    />
                  ) : (
                    <>
                      {isUploading.thumbnail ? (
                        <Card className={classes.thumbNail}>
                          <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                            className="pt-5"
                          >
                            <Typography className="pt-4">Uploading...</Typography>
                          </Grid>
                        </Card>
                      ) : (
                        <CardActionArea
                          className={classes.root}
                          onClick={() => {
                            handleUploadClickThumbnail();
                          }}
                        >
                          {/* <InputLabel htmlFor="thumbnail">
                            Upload image
                          </InputLabel> */}
                          <Avatar className={classes.actionIcon} variant="square">
                            <ImageOutlined />
                          </Avatar>
                          <input
                            accept="image/*"
                            id="contained-button-file"
                            type="file"
                            hidden
                            onChange={onSelectThumbnail}
                            ref={hiddenFileInputThumbnail}
                          />
                        </CardActionArea>
                      )}
                    </>
                  )}
                </Card>
                <Typography variant="overline" align="center" gutterBottom>
                  Thumbnail
                </Typography>
              </FormControl>
            </Grid>
            <Grid item md={4} xs={12}>
              <FormControl variant="outlined" fullWidth>
                <Card variant="outlined" className={classes.root}>
                  {featuredImageUrl ? (
                    <PreviewProfileImage
                      onImageClose={onFeaturedImageClose}
                      src={featuredImageUrl}
                      rectangular
                    />
                  ) : (
                    <>
                      {isUploading.featuredImage ? (
                        <Card className={classes.thumbNail}>
                          <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                            className="pt-5"
                          >
                            <Typography className="pt-4">Uploading...</Typography>
                          </Grid>
                        </Card>
                      ) : (
                        <CardActionArea
                          className={classes.root}
                          onClick={() => {
                            handleUploadClickFeaturedImage();
                          }}
                        >
                          {/* <InputLabel htmlFor="thumbnail">
                            Upload image
                          </InputLabel> */}
                          <Avatar className={classes.actionIcon} variant="square">
                            <ImageOutlined />
                          </Avatar>
                          <input
                            accept="image/*"
                            id="contained-button-file"
                            type="file"
                            hidden
                            onChange={onSelectFeaturedImage}
                            ref={hiddenFileInputFeaturedImage}
                          />
                        </CardActionArea>
                      )}
                    </>
                  )}
                </Card>
                <Typography variant="overline" align="center" gutterBottom>
                  Feature image
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  marginBottom: "10px",
                  marginLeft: "10px",
                }}
              >
                Blog content
              </Typography>
              <ReactQuill
                value={content}
                theme="snow"
                placeholder="Write something awesome..."
                onChange={setContent}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  marginBottom: "10px",
                  marginLeft: "10px",
                }}
              >
                Blog description
              </Typography>
              <ReactQuill
                value={description}
                theme="snow"
                placeholder="Write something awesome..."
                onChange={setDescription}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <Box p={2} display="flex" justifyContent="flex-end">
          <Button
            className={classes.formBtn}
            onClick={props.onClickBack}
            color="primary"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            className={classes.formBtn}
            onClick={() => _addBlog()}
            color="primary"
            disabled={isAdding}
            variant="contained"
          >
            {props.isEditing ? "Update blog" : "Add blog"}
          </Button>
        </Box>
        {imageSrc && (
          <ImageCrop
            src={imageSrc.toString()}
            crop={imageCrop}
            onImageLoaded={onImageLoaded1}
            onCropComplete={onCropComplete}
            onCropChange={onCropChange1}
            onPressDone={onCropDone1}
            onImageClose={onImageClose1}
            open={open}
            onOpen={onOpenImage}
            onClose={onCloseImage}
          />
        )}
        {thumbnailSrc && (
          <ImageCrop
            src={thumbnailSrc.toString()}
            crop={thumbnailCrop}
            onImageLoaded={onThumbnailLoaded}
            onCropComplete={onCropComplete}
            onCropChange={onThumbnailCropChange}
            onPressDone={onThumbnailCropDone}
            onImageClose={onThumbnailClose}
            open={open}
            onOpen={onOpenImage}
            onClose={onCloseThumbnail}
          />
        )}
        {featuredImageSrc && (
          <ImageCrop
            src={featuredImageSrc.toString()}
            crop={featuredImageCrop}
            onImageLoaded={onFeaturedImageLoaded}
            onCropComplete={onCropComplete}
            onCropChange={onFeaturedImageCropChange}
            onPressDone={onFeaturedImageCropDone}
            onImageClose={onFeaturedImageClose}
            open={open}
            onOpen={onOpenImage}
            onClose={onCloseFeaturedImage}
          />
        )}
        <DialogBox
          open={states.showDeleteModal || false}
          onClose={handleDeleteClose}
          onCancel={handleDeleteClose}
          onConfirm={deleteBlog}
          type={"delete"}
          title={`Sure !! Want to delete this blog ?`}
        />

        <Snackbar
          open={snackBar.open}
          autoHideDuration={10000}
          onClose={() => {
            setSnackBar({ ...snackBar, open: false });
          }}
          anchorOrigin={{ horizontal: "right", vertical: "top" }}
        >
          <Alert
            severity={snackBar.severity}
            onClose={() => {
              setSnackBar({ ...snackBar, open: false });
            }}
          >
            {snackBar.message}
          </Alert>
        </Snackbar>
      </Root>
    );
  },
);
